import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStudents } from '../slices/studentsSlice';
import { fetchStaff } from '../slices/staffSlice';
import { fetchRecipients } from '../slices/recipientSlice';
import { format, subMonths, parseISO } from 'date-fns';
import { useToast } from '../context/ToastContext';

import { ar } from 'date-fns/locale';
import { 
  fetchUserInfo, 
  fetchDashboardSummary,
  selectDashboardSummary,
  selectDashboardLoading,
  selectStudentMetrics,
  selectClassMetrics,
  selectBusMetrics,
  selectFinancialMetrics,
  selectDashboardAlerts
} from '../slices/accountSlice';
import { fetchCurrentYearSchoolFees } from '../slices/schoolFeesSlice';
import {
  fetchFinance
} from '../slices/financeSlice';
import {
  addAuthorizedPayer,
} from '../slices/authorizedPayersSlice';
import {
  getPurchaseSummary,
  getDailyPayments,
  getBenefits,
} from '../slices/storePurchasesSlice';
import {
  getMiscellaneousSummary,
} from '../slices/miscellaneousSlice';
import { fetchEmployeeTypes } from '../slices/employeeTypesSlice';
import {
  fetchOptionalCardData,
  saveDashboardCardPrefs,
  selectDashboardCardPrefs,
  OPTIONAL_CARD_ENDPOINTS,
  fetchFinanceRangeStats,
} from '../slices/accountSlice';
import { OPTIONAL_DASHBOARD_CARDS, DEFAULT_DASHBOARD_CARDS, DEFAULT_CARD_IDS, CARD_CATEGORIES } from '../config/dashboardCards';
import { canAccessFeature } from '../utils/featureAccess';
import {
  Typography,
  Box,
  Card,
  Stack,
  Select, MenuItem,
  Button,
  Collapse,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Fab,
  Paper,
  Alert,
   Dialog, DialogTitle, DialogContent, DialogActions,TextField,
  Chip,
  Checkbox,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { PieChart } from '@mui/x-charts/PieChart';
import { motion } from 'framer-motion';
import { 
  IconUsers, 
  IconSchool, 
  IconCash, 
  IconChartLine, 
  IconUserPlus, 
  IconReportAnalytics,
  IconArchive,
  IconBus,
  IconMenu2,
  IconX,
  IconLayoutDashboard,
  IconAlertTriangle,
  IconInfoCircle,
  IconUsersGroup,
  IconPackage,
  IconCreditCard,
  IconCoins,
  IconTruck,
  IconTransfer,
  IconClipboardList,
  IconShoppingCart,
  IconTrendingUp,
  IconChevronDown,
  IconBolt,
  IconPlus
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

const AnimatedCard = motion(Card);
const AnimatedButton = motion(Button);
const MotionPaper = motion(Paper);

const DashboardPage = () => {
  const PHONE_CODE_OPTIONS = [
  { value: '972', label: '+972', flag: '🇵🇸' },
  { value: '970', label: '+970', flag: '🇵🇸' }
];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const userRole = localStorage.getItem('role');
  const isStudent = userRole === 'student';


  // Redux selectors
  const { students } = useSelector((state) => state.students);
  const { data: financeRecords } = useSelector((state) => state.finance);
  const { list: recipentRecord } = useSelector((state) => state.recipients);
  const { data, loading, error } = useSelector(state => state.schoolFees);
  const { list } = useSelector((state) => state.staff);
  
  // Store purchases selectors
  const { summary: purchaseSummary, dailyPayments, benefits } = useSelector(
    (state) => state.storePurchases || {}
  );
  
  // Miscellaneous selectors
  const { summary: miscellaneousSummary } = useSelector(
    (state) => state.miscellaneous || {}
  );

  // Employee types
  const employeeTypesList = useSelector((state) => state.employeeTypes?.items || []);
  
  // New dashboard API selectors
  const dashboardSummary = useSelector(selectDashboardSummary);
  const dashboardLoading = useSelector(selectDashboardLoading);
  const studentMetrics = useSelector(selectStudentMetrics);
  const classMetrics = useSelector(selectClassMetrics);
  const busMetrics = useSelector(selectBusMetrics);
  const financialMetrics = useSelector(selectFinancialMetrics);
  const alerts = useSelector(selectDashboardAlerts);
  const savedCardPrefs = useSelector(selectDashboardCardPrefs);
  const userLoaded = useSelector((state) => !!state.account.user);
  const optionalCardsData = useSelector((state) => state.account.optionalCardsData);
  const financeRangeStats = useSelector((state) => state.account.financeRangeStats);
  const financeRangeLoading = useSelector((state) => state.account.financeRangeLoading);

  // Additional metrics from enhanced API
  const employeeMetrics = dashboardSummary?.employee_metrics;
  const inventoryMetrics = dashboardSummary?.inventory_metrics;

  const [openDestinationModal, setOpenDestinationModal] = useState(false);
  const [newDestination, setNewDestination] = useState({
    name: '',
    phone_number: '',
    phoneCode: '972' // default value
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickAccessExpanded, setQuickAccessExpanded] = useState(false);

  // Optional dashboard cards (added by the user via the corner picker)
  const [enabledCardIds, setEnabledCardIds] = useState(DEFAULT_CARD_IDS);
  const [cardPickerOpen, setCardPickerOpen] = useState(false);
  // null = picker shows every category (opened from the main "+" button); a category
  // key restricts the picker to just that category (opened from a section's own
  // "add cards" button, e.g. the Finance Staff section's date-filterable cards).
  const [cardPickerCategory, setCardPickerCategory] = useState(null);
  const prefsInitialized = useRef(false);
  const savePrefsTimeout = useRef(null);

  // Finance Staff section: shared date-range filter for the date-filterable optional cards
  const canViewFinanceRange = !isStudent && canAccessFeature('/financial');
  const [financeStartDate, setFinanceStartDate] = useState(format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'));
  const [financeEndDate, setFinanceEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const financeRangeFetched = useRef(false);

  useEffect(() => {
    dispatch(fetchUserInfo());
    dispatch(fetchDashboardSummary()); // New API call
    // dispatch(fetchStudents());
    // Don't fetch staff for students
    if (!isStudent) {
      dispatch(fetchStaff());
    }
    dispatch(getPurchaseSummary());
    dispatch(getDailyPayments());
    dispatch(getBenefits({}));
    dispatch(getMiscellaneousSummary());
    dispatch(fetchEmployeeTypes());
    dispatch(fetchRecipients({ school_year: 'current' }));
    dispatch(fetchFinance({ school_year: 'current'}));
    dispatch(fetchCurrentYearSchoolFees());
  }, [dispatch]);

  // Sync the user's saved card selection once it arrives, and lazily fetch data only
  // for the enabled cards that need a dedicated endpoint call. New users (no saved
  // prefs yet) start with the 10 default cards; existing prefs are used verbatim so
  // a user who has hidden a default card or added optional ones keeps that choice.
  useEffect(() => {
    if (prefsInitialized.current || isStudent || !userLoaded) return;
    prefsInitialized.current = true;
    // Array (even empty) = the user explicitly saved a selection before, respect it as-is.
    // Anything else (null/undefined) = never customized, fall back to the defaults.
    const initial = Array.isArray(savedCardPrefs) ? savedCardPrefs : DEFAULT_CARD_IDS;
    setEnabledCardIds(initial);
    initial.forEach((id) => {
      const def = OPTIONAL_DASHBOARD_CARDS.find((c) => c.id === id);
      if (def?.needsFetch) {
        dispatch(fetchOptionalCardData(id));
      }
    });
  }, [userLoaded, savedCardPrefs, isStudent, dispatch]);

  const persistCardPrefs = (ids) => {
    if (savePrefsTimeout.current) clearTimeout(savePrefsTimeout.current);
    savePrefsTimeout.current = setTimeout(() => {
      dispatch(saveDashboardCardPrefs(ids));
    }, 500);
  };

  const toggleCard = (id) => {
    setEnabledCardIds((prev) => {
      const isEnabled = prev.includes(id);
      const next = isEnabled ? prev.filter((c) => c !== id) : [...prev, id];
      if (!isEnabled) {
        const def = OPTIONAL_DASHBOARD_CARDS.find((c) => c.id === id);
        if (def?.needsFetch) {
          dispatch(fetchOptionalCardData(id));
        }
      }
      persistCardPrefs(next);
      return next;
    });
  };

  // Finance Staff section: ids of optional cards that belong in the date-filterable
  // section instead of the general grid (see config/dashboardCards.js).
  const dateFilterableIds = useMemo(
    () => OPTIONAL_DASHBOARD_CARDS.filter((c) => c.dateFilterable).map((c) => c.id),
    []
  );

  // Auto-fetch once (with the default this-month range) for any user who can see the
  // Finance Staff section, since the 3 pinned cards there are always shown.
  useEffect(() => {
    if (!canViewFinanceRange || financeRangeFetched.current) return;
    financeRangeFetched.current = true;
    dispatch(fetchFinanceRangeStats({ startDate: financeStartDate, endDate: financeEndDate }));
  }, [canViewFinanceRange, dispatch, financeStartDate, financeEndDate]);

  const applyFinanceRangeFilter = () => {
    dispatch(fetchFinanceRangeStats({ startDate: financeStartDate, endDate: financeEndDate }));
  };


  // Dates and helpers
  const today = new Date().toISOString().slice(0, 10);
  const todayISO = new Date().toISOString().split('T')[0];
  const isInCurrentMonth = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  };
  const toAmount = (val) => parseFloat(val) || 0;

  const { monthlyPayments, busCosts } = useMemo(() => {
    const todayPayments = (financeRecords || []).filter(p => p.date === today);
    const todayRecipients = (recipentRecord || []).filter(r => r.date === today);

    const monthlyPayments = [
      ...todayPayments.map(p => toAmount(p.amount)),
    ].reduce((sum, val) => sum + val, 0);

    const busPaymentsThisMonth = (financeRecords || []).filter(
      p => p.type === "باص" && isInCurrentMonth(p.date)
    );
    const busCosts = busPaymentsThisMonth.reduce((sum, p) => sum + toAmount(p.amount), 0);

    return { monthlyPayments, busCosts };
  }, [financeRecords, recipentRecord, today]);

  const getLastFiveMonths = (dates) => {
    const latest = dates.length
      ? dates.reduce((max, d) => (d > max ? d : max), dates[0])
      : new Date();

    return Array.from({ length: 5 }).map((_, i) => {
      const date = subMonths(latest, 4 - i);
      const key = format(date, 'yyyy-MM');
      const label = format(date, 'LLLL', { locale: ar });
      return { key, label };
    });
  };

  const buildMonthlyData = (recipentRecord = [], financeRecords = []) => {
    const allDates = [
      ...recipentRecord.map(r => new Date(r.date)),
      ...financeRecords.map(p => new Date(p.date))
    ];

    const months = getLastFiveMonths(allDates);

    const incomeMap = recipentRecord.reduce((acc, r) => {
      if (!r.received) return acc;
      const key = format(parseISO(r.date), 'yyyy-MM');
      acc[key] = (acc[key] || 0) + parseFloat(r.amount || 0);
      return acc;
    }, {});

    const expensesMap = financeRecords.reduce((acc, p) => {
      const key = format(parseISO(p.date), 'yyyy-MM');
      acc[key] = (acc[key] || 0) + parseFloat(p.amount || 0);
      return acc;
    }, {});

    const monthlyData = months.map(({ key, label }) => ({
      month: label,
      income: incomeMap[key] || 0,
      expenses: expensesMap[key] || 0
    }));

    return monthlyData;
  };

  // Enhanced dashboard data using new API
  const dashboardData = useMemo(() => {
    // Fallback to original calculations if new API data is not available
    const fallbackData = {
      students: {
        active: students?.filter(s => !s.is_archived).length || 0,
        archived: students?.filter(s => s.is_archived).length || 0,
        closed: recipentRecord?.filter(r => r.received).length || 0,
        open: data ? students?.length - (recipentRecord?.filter(r => r.received).length || 0) : 0,
        // Fix: Use received payments like original version
        closedAmount: recipentRecord
          ?.filter(r => r.received)
          .reduce((sum, r) => sum + toAmount(r.amount), 0) || 0,
        openAmount: data?.total_school_fees - (recipentRecord
          ?.filter(r => r.received)
          .reduce((sum, r) => sum + toAmount(r.amount), 0) || 0) || 0,
      },
      staff: isStudent ? 0 : (list?.filter(s => !s.is_archived).length || 0),
      finance: {
        monthlyPayments,
        busCosts
      },
      payments: {
        totalReceived: recipentRecord
          ?.filter(r => r.received && r.date === todayISO)
          .reduce((sum, r) => sum + toAmount(r.amount), 0) || 0,
        totalPending: recipentRecord
          ?.filter(r => !r.received)
          .reduce((sum, r) => sum + toAmount(r.amount), 0) || 0,
        receivedPercentage: recipentRecord?.length > 0
          ? ((recipentRecord.filter(r => r.received).length / recipentRecord.length) * 100).toFixed(1)
          : 0,
      },
      employee: {
        closedAmount: list
          ?.filter(s => !s.is_archived)
          .reduce((sum, r) => sum + toAmount(r.total_paid_this_month), 0) || 0,
        openAmount: list
          ?.filter(s => !s.is_archived)
          .reduce((sum, r) => sum + getRemainingSalary(r), 0) || 0,
      },
      monthlyData: buildMonthlyData(recipentRecord, financeRecords),
    };

    // Use new API data when available, fallback to original calculations
    if (dashboardSummary) {
      return {
        ...fallbackData,
        students: {
          ...fallbackData.students,
          active: studentMetrics?.active_students || fallbackData.students.active,
          archived: studentMetrics?.archived_students || fallbackData.students.archived,
          total: studentMetrics?.total_students || fallbackData.students.active + fallbackData.students.archived,
          busStudents: studentMetrics?.bus_students || 0,
          maleStudents: studentMetrics?.male_students || 0,
          femaleStudents: studentMetrics?.female_students || 0,
          utilization: studentMetrics?.utilization_percentage || 0,
          maxStudents: studentMetrics?.max_students || 0,
          remainingSlots: studentMetrics?.remaining_slots || 0,
          // Keep original calculation for received payments
          closedAmount: fallbackData.students.closedAmount,
          openAmount: fallbackData.students.openAmount,
        },
        employees: isStudent ? {
          total: 0,
          active: 0,
          archived: 0,
          typeDistribution: [],
          recentActivity: {}
        } : {
          total: employeeMetrics?.total_employees || list?.length || 0,
          active: employeeMetrics?.active_employees || list?.filter(s => !s.is_archived).length || 0,
          archived: employeeMetrics?.archived_employees || list?.filter(s => s.is_archived).length || 0,
          typeDistribution: employeeMetrics?.type_distribution || [],
          recentActivity: employeeMetrics?.recent_activity || {}
        },
        inventory: {
          available: inventoryMetrics?.available || false,
          totalItems: inventoryMetrics?.total_items || 0,
          totalStock: inventoryMetrics?.total_stock || 0,
          lowStockItems: inventoryMetrics?.low_stock_items || 0,
          outOfStockItems: inventoryMetrics?.out_of_stock_items || 0,
          topItems: inventoryMetrics?.top_items || [],
          low_quantity_items: inventoryMetrics?.low_quantity_items || []
        },
        classes: {
          total: classMetrics?.total_classes || 0,
          avgStudentsPerClass: classMetrics?.avg_students_per_class || 0,
          distribution: classMetrics?.distribution || []
        },
        buses: {
          total: busMetrics?.total_buses || 0,
          internal: busMetrics?.internal_buses || 0,
          external: busMetrics?.external_buses || 0,
          totalCapacity: busMetrics?.total_capacity || 0,
          studentsOnBuses: busMetrics?.students_on_buses || 0,
          utilization: busMetrics?.utilization_percentage || 0,
          availableCapacity: busMetrics?.available_capacity || 0
        },
        financial: {
          ...fallbackData.finance,
          totalCollected: financialMetrics?.total_collected || 0,
          totalOutstanding: financialMetrics?.total_outstanding || 0,
          studentsWithPayments: financialMetrics?.students_with_payments || 0,
          studentsWithOutstanding: financialMetrics?.students_with_outstanding || 0,
          averagePayment: financialMetrics?.average_payment || 0,
          collectionRate: financialMetrics?.collection_rate || 0,
          totalPaymentsCount: financialMetrics?.total_payments_count || 0,
          totalRecipientsCount: financialMetrics?.total_recipients_count || 0,
          paymentMethods: financialMetrics?.payment_methods || {},
          recentPaymentsAmount: financialMetrics?.recent_payments_amount || 0,
          allTimePayments: financialMetrics?.all_time_payments || 0,
          allTimeRecipients: financialMetrics?.all_time_recipients || 0,
          avgPaymentSize: financialMetrics?.avg_payment_size || 0
        },
        summary: dashboardSummary.summary || {},
        recentActivity: dashboardSummary.recent_activity || {}
      };
    }

    return fallbackData;
  }, [dashboardSummary, studentMetrics, classMetrics, busMetrics, financialMetrics, students, recipentRecord, data, list, financeRecords, monthlyPayments, busCosts, todayISO]);

  // Calculate daily/monthly totals
  const dailyTotals = useMemo(() => {
    const monthlyReceived = recipentRecord
      ?.filter(r => r.received && isInCurrentMonth(r.date))
      .reduce((sum, r) => sum + toAmount(r.amount), 0) || 0;

    const dailyExpenses = (financeRecords || [])
      .filter(p => isInCurrentMonth(p.date))
      .reduce((sum, p) => sum + toAmount(p.amount), 0);

    return { dailyReceived: monthlyReceived, dailyExpenses };
  }, [recipentRecord, financeRecords, today]);

  // Enhanced stat cards with original daily cards - filtered for students
  const statCards = useMemo(() => {
    const allCards = [
      {
        id: 'monthly_receipts',
        title: 'إجمالي المقبوضات الشهرية',
        value: `₪${dailyTotals.dailyReceived.toLocaleString('en-US')}`,
        icon: <IconCash size={32} />,
        color: '#43A047',
        hideForStudents: true
      },
      {
        id: 'monthly_expenses',
        title: 'إجمالي المصروفات الشهرية',
        value: `₪${dailyTotals.dailyExpenses.toLocaleString('en-US')}`,
        icon: <IconCash size={32} />,
        color: '#F44336',
        hideForStudents: true
      },
      {
        id: 'students',
        title: 'الطلاب',
        value: dashboardData.students?.active || students?.filter(s => !s.is_archived).length || 0,
        subtitle: `${dashboardData.students?.archived || students?.filter(s => s.is_archived).length || 0} طالب مؤرشف`,
        additionalInfo: {
          total: dashboardData.students?.total || (students?.length || 0),
          busStudents: dashboardData.students?.busStudents || 0,
          maleStudents: dashboardData.students?.maleStudents || 0,
          femaleStudents: dashboardData.students?.femaleStudents || 0,
          utilization: dashboardData.students?.utilization || 0,
          maxStudents: dashboardData.students?.maxStudents || 0,
          remainingSlots: dashboardData.students?.remainingSlots || 0,
        },
        icon: <IconUsers size={32} />,
        color: '#009688',
        enhanced: true,
        hideForStudents: true
      },
      {
        id: 'staff',
        title: 'الموظفون',
        value: list?.filter(s => !s.is_archived).length || 0,
        employeeTypes: (() => {
          const active = list?.filter(s => !s.is_archived) || [];
          const typeMap = Object.fromEntries(employeeTypesList.map(t => [t.id, t.display_value || t.name]));
          const counts = {};
          active.forEach(s => {
            const label = (typeof s.employee_type === 'string' ? typeMap[s.employee_type] : s.employee_type?.display_value || s.employee_type?.name) || 'غير محدد';
            counts[label] = (counts[label] || 0) + 1;
          });
          return counts;
        })(),
        icon: <IconSchool size={32} />,
        color: '#673AB7',
        hideForStudents: true
      },
      {
        id: 'bus_costs',
        title: 'تكاليف الباصات',
        value: `₪${dashboardData.finance.busCosts.toLocaleString('en-US')}`,
        icon: <IconBus size={32} />,
        color: '#FFB74D',
        hideForStudents: true
      },
      {
        id: 'today_purchases',
        title: 'المشتريات اليوم',
        value: `₪${(purchaseSummary?.today_amount || 0).toLocaleString('en-US')}`,
        subtitle: `${purchaseSummary?.today_purchases || 0} عملية شراء`,
        icon: <IconPackage size={32} />,
        color: '#9C27B0',
        route: '/store-purchases',
        hideForStudents: true
      },
      {
        id: 'today_payments',
        title: 'المدفوعات اليوم',
        value: `₪${(dailyPayments?.total_amount || 0).toLocaleString('en-US')}`,
        subtitle: `${dailyPayments?.payment_count || 0} دفعة`,
        icon: <IconCash size={32} />,
        color: '#4CAF50',
        route: '/store-purchases',
        hideForStudents: true
      },
      {
        id: 'profits',
        title: 'الأرباح',
        value: `₪${(benefits?.benefits || 0).toLocaleString('en-US')}`,
        subtitle: `المدفوعات - التكاليف`,
        icon: <IconTrendingUp size={32} />,
        color: '#FF9800',
        route: '/store-purchases',
        hideForStudents: true
      },
      {
        id: 'misc_items',
        title: 'المتفرقات',
        value: `${miscellaneousSummary?.total_items || 0}`,
        subtitle: `₪${Number(miscellaneousSummary?.total_amount || 0).toLocaleString('en-US')}`,
        icon: <IconPackage size={32} />,
        color: '#E91E63',
        route: '/miscellaneous',
        hideForStudents: true
      },
      {
        id: 'misc_remaining',
        title: 'المتفرقات المتبقية',
        value: `₪${Number(miscellaneousSummary?.remaining_amount || 0).toLocaleString('en-US')}`,
        subtitle: `${miscellaneousSummary?.total_items || 0} عنصر`,
        icon: <IconCash size={32} />,
        color: '#FF5722',
        route: '/miscellaneous',
        hideForStudents: true
      }
    ];
    
    // Filter out cards that students shouldn't see
    return isStudent ? allCards.filter(card => !card.hideForStudents) : allCards;
  }, [dailyTotals, dashboardData, students, list, purchaseSummary, dailyPayments, benefits, miscellaneousSummary, isStudent, employeeTypesList]);

  const totalRemainingSalaries = useMemo(() => {
    return (list || [])
      .filter(s => !s.is_archived)
      .reduce((sum, s) => sum + getRemainingSalary(s), 0);
  }, [list]);

  // Builds { value, subtitle } for a user-enabled optional card. Tier-1 ids read from data
  // already in the store (dashboardData/dashboardSummary); Tier-2/3 ids read from the lazily
  // fetched optionalCardsData (keyed by endpoint URL, see accountSlice.OPTIONAL_CARD_ENDPOINTS).
  const buildOptionalCardValue = (id) => {
    const fmt = (n) => `₪${Number(n || 0).toLocaleString('en-US')}`;
    const fetched = OPTIONAL_CARD_ENDPOINTS[id] ? optionalCardsData[OPTIONAL_CARD_ENDPOINTS[id]] : null;

    switch (id) {
      case 'bus_utilization':
        return { value: `${dashboardData.buses?.utilization || 0}%`, subtitle: `مقاعد متاحة: ${dashboardData.buses?.availableCapacity || 0}` };
      case 'avg_students_per_class':
        return { value: dashboardData.classes?.avgStudentsPerClass || 0, subtitle: `${dashboardData.classes?.total || 0} صف` };
      case 'class_distribution': {
        const top = dashboardData.classes?.distribution?.[0];
        return { value: dashboardData.classes?.total || 0, subtitle: top ? `الأكبر: ${top.name} (${top.active_student_count})` : 'لا توجد صفوف' };
      }
      case 'employee_type_distribution': {
        const dist = dashboardData.employees?.typeDistribution || [];
        const top = dist[0];
        return { value: dist.length, subtitle: top ? `الأكثر: ${top.type} (${top.count})` : 'لا يوجد تصنيف' };
      }
      case 'new_students_30d':
        return { value: dashboardData.recentActivity?.new_students || 0, subtitle: 'خلال آخر ٣٠ يوم' };
      case 'new_employees_30d':
        return { value: dashboardData.recentActivity?.new_employees || 0, subtitle: 'خلال آخر ٣٠ يوم' };
      case 'collection_rate':
        return { value: `${dashboardData.financial?.collectionRate || 0}%`, subtitle: 'نسبة الطلاب الذين دفعوا' };
      case 'avg_payment_size':
        return { value: fmt(dashboardData.financial?.avgPaymentSize), subtitle: 'متوسط قيمة الدفعة الواحدة' };
      case 'payment_methods_breakdown': {
        const methods = Object.entries(dashboardData.financial?.paymentMethods || {});
        const top = methods.sort((a, b) => (b[1]?.amount || 0) - (a[1]?.amount || 0))[0];
        return { value: methods.length, subtitle: top ? `الأكثر استخداماً: ${top[0]}` : 'لا توجد بيانات' };
      }
      case 'total_outstanding':
        return { value: fmt(dashboardData.financial?.totalOutstanding), subtitle: `${dashboardData.financial?.studentsWithOutstanding || 0} طالب` };
      case 'remaining_salaries':
        return { value: fmt(totalRemainingSalaries), subtitle: 'إجمالي الرواتب المتبقية للموظفين' };
      case 'employee_dashboard_stats':
        return { value: fmt(fetched?.salary_stats?.avg_salary), subtitle: `متوسط الراتب - إجمالي: ${fmt(fetched?.salary_stats?.total_salaries)}` };
      case 'payment_dashboard_stats':
        return { value: fetched?.recipient_stats?.pending_receipts ?? '—', subtitle: 'سندات قبض معلقة' };
      case 'daily_payments_today':
        return { value: fmt(fetched?.daily_payments?.amount), subtitle: `${fetched?.daily_payments?.count || 0} سند صرف اليوم` };
      case 'daily_receipts_today':
        return { value: fmt(fetched?.daily_receipts?.amount), subtitle: `${fetched?.daily_receipts?.count || 0} سند قبض اليوم` };
      case 'today_absences':
        return { value: fetched?.total_absences_today ?? '—', subtitle: `موظفون: ${fetched?.employee_absences_today || 0} - معلمون: ${fetched?.teacher_absences_today || 0}` };
      case 'events_summary':
        return { value: fetched?.upcoming_events ?? '—', subtitle: `فعاليات اليوم: ${fetched?.today_events || 0}` };
      case 'total_inventory_value':
        return { value: fmt(fetched?.total_inventory_value), subtitle: 'القيمة الإجمالية لمخزون المستودع' };
      case 'low_stock_items': {
        const items = dashboardData.inventory?.low_quantity_items || [];
        const top = items[0];
        return { value: items.length, subtitle: top ? `الأقل مخزوناً: ${top.name} (${top.count})` : 'لا توجد عناصر منخفضة المخزون' };
      }
      case 'bus_stats':
        return { value: dashboardData.buses?.total || 0, subtitle: `داخلي: ${dashboardData.buses?.internal || 0} - خارجي: ${dashboardData.buses?.external || 0} - السعة: ${dashboardData.buses?.totalCapacity || 0}` };
      case 'receipts_collected_range':
        return { value: fmt(financeRangeStats?.receipts_collected?.total_amount), subtitle: `${financeRangeStats?.receipts_collected?.count || 0} سند محصل` };
      case 'expenses_range':
        return { value: fmt(financeRangeStats?.expenses?.total_amount), subtitle: `${financeRangeStats?.expenses?.count || 0} سند صرف` };
      case 'collection_rate_range':
        return { value: `${financeRangeStats?.collection_rate || 0}%`, subtitle: 'نسبة السندات المحصلة خلال الفترة' };
      case 'avg_payment_size_range':
        return { value: fmt(financeRangeStats?.installments?.avg_amount), subtitle: 'متوسط قيمة السند خلال الفترة' };
      case 'payment_methods_breakdown_range': {
        const methods = Object.entries(financeRangeStats?.payment_methods || {});
        const top = methods.sort((a, b) => (b[1]?.amount || 0) - (a[1]?.amount || 0))[0];
        return { value: methods.length, subtitle: top ? `الأكثر استخداماً: ${top[0]}` : 'لا توجد بيانات' };
      }
      case 'salary_stats_range':
        return { value: fmt(financeRangeStats?.salaries?.avg), subtitle: `أعلى: ${fmt(financeRangeStats?.salaries?.max)} - أدنى: ${fmt(financeRangeStats?.salaries?.min)}` };
      default:
        return { value: '—', subtitle: '' };
    }
  };

  const optionalCards = useMemo(() => {
    if (isStudent) return [];
    return enabledCardIds
      .filter((id) => !dateFilterableIds.includes(id))
      .map((id) => {
        const def = OPTIONAL_DASHBOARD_CARDS.find((c) => c.id === id);
        if (!def) return null;
        const { value, subtitle } = buildOptionalCardValue(id);
        return { title: def.title, value, subtitle, icon: def.icon, color: def.color };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledCardIds, dateFilterableIds, dashboardData, optionalCardsData, totalRemainingSalaries, isStudent]);

  // Finance Staff section: optional date-filterable cards the user has enabled via
  // the picker. The 3 "pinned" concepts (installments/salaries/uncollected receipts)
  // are rendered separately as the existing detailed pie-chart widgets, not here.
  const financeRangeCards = useMemo(() => {
    if (isStudent || !canViewFinanceRange) return [];
    // Sorted by OPTIONAL_DASHBOARD_CARDS' fixed order (not enabledCardIds' insertion
    // order) so this section's layout stays stable regardless of when the user added
    // each card - e.g. payment_methods_breakdown_range always renders first.
    return OPTIONAL_DASHBOARD_CARDS
      .filter((def) => dateFilterableIds.includes(def.id) && enabledCardIds.includes(def.id))
      .map((def) => {
        const { value, subtitle } = buildOptionalCardValue(def.id);
        return { title: def.title, value, subtitle, icon: def.icon, color: def.color };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledCardIds, dateFilterableIds, isStudent, canViewFinanceRange, financeRangeStats]);

  const quickAccessButtons = useMemo(() => {
    const allButtons = [
      { label: 'سند قبض', icon: <IconCash size={32} />, color: '#43A047', route: '/students/payment', hideForStudents: true },
      { label: 'سند صرف', icon: <IconCash size={32} />, color: '#F44336', route: '/finance/create?type=سند صرف', hideForStudents: true },
      { label: 'إضافة طالب', icon: <IconUserPlus size={32} />, color: '#009688', route: '/students/create', hideForStudents: true },
      { label: 'إضافة موظف', icon: <IconUserPlus size={32} />, color: '#673AB7', route: '/staff/create', hideForStudents: true },
      { label: 'إضافة صف', icon: <IconUserPlus size={32} />, color: '#2196F3', route: '/classes/create', hideForStudents: true },
      { label: 'إضافة باص', icon: <IconUserPlus size={32} />, color: '#FF9800', route: '/buses/create', hideForStudents: true },
      { label: 'إضافة جهة الصرف المعتمدة', icon: <IconUserPlus size={32} />, color: '#795548', route: '/buses/create', hideForStudents: true },
    ];
    
    // Filter out buttons that students shouldn't see
    return isStudent ? [] : allButtons;
  }, [isStudent]);



  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  function getRemainingSalary(staff) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // Adding 1 because salary_exceptions use 1-12 format
    
    const base = Number(staff.base_salary || staff.salary || 0);
    
    const bonuses = Array.isArray(staff.virtual_transactions)
      ? staff.virtual_transactions
          .filter(v => v.direction === 'credit')
          .reduce((sum, v) => sum + Number(v.amount || 0), 0)
      : 0;
    
    // Check for salary exceptions in current month
    const hasUnpaidLeave = Array.isArray(staff.salary_exceptions) && 
        staff.salary_exceptions.some(exception => 
            exception.year === currentYear && 
            exception.month === currentMonth && 
            exception.exception_type === 'unpaid_leave'
        );
    
    // If there's unpaid leave, don't include base salary
    const effectiveBaseSalary = hasUnpaidLeave ? 0 : base;
    
    const total = effectiveBaseSalary + bonuses;
    const paid = Number(staff.total_paid_this_month || staff.paid || 0);
    return total - paid;
}

  const handleAddDestination = async () => {
  if (newDestination.name?.trim()) {
    try {
      await dispatch(addAuthorizedPayer({
        name: newDestination.name.trim(),
        display_value: newDestination.name.trim(),
        phone_number: newDestination.phone_number?.trim() || null
      })).unwrap();

      setNewDestination({ name: '', phone_number: '' });
      showToast('✅ تم إضافة جهة الصرف بنجاح');
    } catch (error) {
      showToast('❌ حدث خطأ أثناء إضافة جهة الصرف');
    }
  } else {
    showToast('⚠️ الرجاء إدخال الاسم');
  }
};

  // Alert component for dashboard notifications
  const AlertsSection = () => {
    if (!alerts || alerts.length === 0) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 3 }}>
          <Stack spacing={2}>
            {alerts.map((alert, index) => (
              <Alert
                key={index}
                severity={alert.type === 'warning' ? 'warning' : 'info'}
                icon={alert.type === 'warning' ? <IconAlertTriangle /> : <IconInfoCircle />}
                sx={{
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontWeight: 'bold'
                  }
                }}
              >
                {alert.message}
              </Alert>
            ))}
          </Stack>
        </Box>
      </motion.div>
    );
  };

  // Payment methods pie chart data
  const paymentMethodsData = useMemo(() => {
    if (!dashboardData.financial?.paymentMethods) return [];
    
    const colors = {
  'نقداً': '#2E7D32',         // أخضر (ثقة)
  'فيزا': '#1565C0',          // أزرق أوضح
  'شيك': '#FB8C00',           // برتقالي ذهبي
  'تحويل بنكي': '#8E24AA'     // بنفسجي ملكي
};

    
    return Object.entries(dashboardData.financial.paymentMethods).map(([method, data]) => ({
      name: method,
      value: data.amount,
      count: data.count,
      color: colors[method] || '#757575'
    }));
  }, [dashboardData.financial?.paymentMethods]);

  return (
    // 🎨 Design System Applied
    <Box sx={{
      direction: 'rtl',
      fontFamily: 'Cairo, Tajawal, sans-serif',
      p: { xs: 2, md: 3 },
      backgroundColor: '#F8F9FF',
      minHeight: '100vh',
    }} dir='rtl'>
     <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header Section */}
      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2.5,
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconLayoutDashboard size={30} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              color="#fff"
              sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              لوحة التحكم الإدارية
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                mt: 0.25,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              نظرة شاملة على أداء المدرسة والمؤشرات المالية
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          {dashboardData.summary?.active_year && (
            <Chip
              label={`العام الدراسي: ${dashboardData.summary.active_year}`}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '& .MuiChip-label': { fontWeight: 700 }
              }}
            />
          )}
          {!isStudent && (
            <IconButton
              onClick={() => { setCardPickerCategory(null); setCardPickerOpen(true); }}
              aria-label="إضافة بطاقات"
              title="إضافة بطاقات للوحة التحكم"
              sx={{
                bgcolor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            >
              <IconPlus size={20} />
            </IconButton>
          )}
        </Stack>
      </MotionPaper>

      {/* Mobile Menu Button */}
      {isMobile && (
        <Fab 
          color="primary" 
          aria-label="menu"
          onClick={toggleMobileMenu}
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            left: 20, 
            zIndex: 1000,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #00BFFF 0%, #3730A3 60%, #5B21F4 100%)',
            }
          }}
        >
          <IconMenu2 />
        </Fab>
      )}

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={isMobile && mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          '& .MuiDrawer-paper': {
            width: '85%',
            maxWidth: '320px',
            padding: 2,
            backgroundColor: '#fff'
          },
          display: { xs: 'block', sm: 'none' }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #eee',
          mb: 2
        }}>
          <Typography variant="h6" fontWeight="bold">القائمة</Typography>
          <IconButton onClick={toggleMobileMenu}>
            <IconX />
          </IconButton>
        </Box>
        
        <Stack spacing={2}>
          {quickAccessButtons.map((button, index) => (
            <Button
              key={index}
              variant='contained'
              startIcon={button.icon}
              fullWidth
              onClick={() => {
                if (button.label === 'إضافة جهة الصرف المعتمدة') {
                  setOpenDestinationModal(true);
                } else if (button.route) {
                  navigate(button.route);
                  setMobileMenuOpen(false);
                }
              }}
              sx={{ 
                backgroundColor: button.color,
                color: 'white',
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontWeight: 'bold',
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                '&:hover': {
                  backgroundColor: button.color,
                  filter: 'brightness(0.9)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              {button.label}
            </Button>
          ))}
        </Stack>
      </Drawer>

      {/* Add-Cards picker drawer - opened via the "+" button in the header */}
      <Drawer
        anchor="left"
        open={cardPickerOpen}
        onClose={() => setCardPickerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: { xs: '90%', sm: 380 }, p: 2 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {cardPickerCategory ? `إضافة بطاقات - ${CARD_CATEGORIES[cardPickerCategory]}` : 'إضافة بطاقات للوحة التحكم'}
          </Typography>
          <IconButton onClick={() => setCardPickerOpen(false)}>
            <IconX />
          </IconButton>
        </Box>
        {Object.entries(CARD_CATEGORIES)
          .filter(([categoryKey]) => !cardPickerCategory || categoryKey === cardPickerCategory)
          .map(([categoryKey, categoryLabel]) => {
          const cardsInCategory = [
            ...DEFAULT_DASHBOARD_CARDS.filter(c => c.category === categoryKey),
            ...OPTIONAL_DASHBOARD_CARDS.filter(c => c.category === categoryKey),
          ];
          if (cardsInCategory.length === 0) return null;
          return (
            <Box key={categoryKey} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#6B7280', fontWeight: 700, mb: 0.5 }}>
                {categoryLabel}
              </Typography>
              <Stack spacing={0.5}>
                {cardsInCategory.map(cardDef => (
                  <Box
                    key={cardDef.id}
                    onClick={() => toggleCard(cardDef.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#F5F5FA' }
                    }}
                  >
                    <Checkbox
                      checked={enabledCardIds.includes(cardDef.id)}
                      onChange={() => toggleCard(cardDef.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>{cardDef.title}</Typography>
                    {DEFAULT_CARD_IDS.includes(cardDef.id) && (
                      <Chip label="افتراضية" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                ))}
              </Stack>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          );
        })}
      </Drawer>

      {/* Quick Access Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
      >
        <Box sx={{ display: { xs: 'none', sm: 'block' }, mb: 4 }}>
          <Button
            variant="text"
            onClick={() => setQuickAccessExpanded(prev => !prev)}
            startIcon={<IconBolt size={20} />}
            endIcon={
              <IconChevronDown
                size={18}
                style={{
                  transition: 'transform 0.25s ease',
                  transform: quickAccessExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            }
            sx={{
              bgcolor: '#EEF2FF',
              color: '#4338CA',
              py: 1,
              px: 2.5,
              borderRadius: 2.5,
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              boxShadow: 'none',
              mb: 2,
              '& .MuiButton-startIcon': { color: '#4338CA' },
              '&:hover': { bgcolor: '#E0E7FF', boxShadow: 'none' },
              transition: 'all 0.2s ease'
            }}
          >
            الإجراءات السريعة
          </Button>
          <Collapse in={quickAccessExpanded}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(4, 1fr)'
              },
              gap: 2,
              pt: 0.5
            }}>
              {quickAccessButtons.map((button, index) => (
                <AnimatedButton
                  key={index}
                  onClick={() => {
                    if (button.label === 'إضافة جهة الصرف المعتمدة') {
                      setOpenDestinationModal(true);
                    } else if (button.route) {
                      navigate(button.route);
                    }
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    bgcolor: '#fff',
                    color: '#1E1B4B',
                    p: 1.5,
                    borderRadius: 3,
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    textTransform: 'none',
                    textAlign: 'right',
                    minHeight: 64,
                    '&:hover': {
                      bgcolor: '#fff',
                      borderColor: '#4338CA',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(67,56,202,0.12)',
                    },
                    transition: 'all 0.25s ease'
                  }}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.04 }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${button.color}1A`,
                      color: button.color,
                    }}
                  >
                    {button.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: '#1E1B4B',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      lineHeight: 1.3,
                    }}
                  >
                    {button.label}
                  </Typography>
                </AnimatedButton>
              ))}
            </Box>
          </Collapse>
        </Box>
      </motion.div>

      {/* Statistics Cards */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.25, ease: 'easeOut' }}
      >
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)'
          },
          gap: 3,
          mb: 4
        }}>
          {[...statCards.filter(c => enabledCardIds.includes(c.id)), ...optionalCards].map((card, index) => (
            <AnimatedCard
              key={index}
              elevation={0}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: index * 0.06, ease: 'easeOut' }}
              onClick={() => card.route && navigate(card.route)}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: '#fff',
                color: '#1E1B4B',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
                minHeight: card.enhanced ? 260 : 200,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                position: 'relative',
                overflow: 'hidden',
                cursor: card.route ? 'pointer' : 'default',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
                },
                transition: 'all 0.25s ease'
              }}>

              {/* Header with icon and title */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', zIndex: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${card.color}1A`,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                    lineHeight: 1.3,
                    fontWeight: 700,
                    color: '#6B7280',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  {card.title}
                </Typography>
              </Box>

              {/* Main value */}
              <Typography
                fontWeight={800}
                sx={{
                  fontSize: { xs: '1.5rem', sm: '1.6rem', md: '1.8rem' },
                  zIndex: 2,
                  color: '#1E1B4B',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                {card.value}
              </Typography>
              
              {/* Enhanced student details */}
              {card.enhanced && card.additionalInfo && (
                <Box sx={{ width: '100%', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>إجمالي:</Typography>
                    <Typography variant="caption" sx={{ color: '#1E1B4B', fontWeight: 800 }}>
                      {card.additionalInfo.total}
                    </Typography>
                  </Box>

                  {card.additionalInfo.busStudents > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>طلاب مسجلين بالنقل:</Typography>
                      <Typography variant="caption" sx={{ color: '#1E1B4B', fontWeight: 800 }}>
                        {card.additionalInfo.busStudents}
                      </Typography>
                    </Box>
                  )}

                  {(card.additionalInfo.maleStudents > 0 || card.additionalInfo.femaleStudents > 0) && (() => {
                    const total = card.additionalInfo.maleStudents + card.additionalInfo.femaleStudents;
                    const malePct = total > 0 ? ((card.additionalInfo.maleStudents / total) * 100).toFixed(0) : 0;
                    const femalePct = total > 0 ? ((card.additionalInfo.femaleStudents / total) * 100).toFixed(0) : 0;
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>ذكور/إناث:</Typography>
                        <Typography variant="caption" sx={{ color: '#1E1B4B', fontWeight: 800 }}>
                          {card.additionalInfo.maleStudents} ({malePct}%) / {card.additionalInfo.femaleStudents} ({femalePct}%)
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              )}

              {/* Employee type breakdown */}
              {card.employeeTypes && Object.keys(card.employeeTypes).length > 0 && (
                <Box sx={{ width: '100%', zIndex: 2 }}>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 700, display: 'block', mb: 0.5, borderBottom: '1px solid #E5E7EB', pb: 0.5 }}>
                    نوع الموظف
                  </Typography>
                  {Object.entries(card.employeeTypes).map(([type, count]) => (
                    <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>{type}:</Typography>
                      <Typography variant="caption" sx={{ color: '#1E1B4B', fontWeight: 800 }}>{count}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Regular subtitle for non-enhanced cards */}
              {card.subtitle && !card.enhanced && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6B7280',
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                    lineHeight: 1.2,
                    zIndex: 2,
                    fontWeight: 600
                  }}
                >
                  {card.subtitle}
                </Typography>
              )}

              {/* Archived students info for enhanced student card */}
              {card.enhanced && card.subtitle && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6B7280',
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                    lineHeight: 1.2,
                    zIndex: 2,
                    mt: 1,
                    pt: 1,
                    borderTop: '1px solid #E5E7EB',
                    width: '100%',
                    fontWeight: 600
                  }}
                >
                  {card.subtitle}
                </Typography>
              )}

              {/* Progress bar for utilization */}
              {card.enhanced && card.additionalInfo?.utilization > 0 && (
                <Box sx={{
                  width: '100%',
                  height: 6,
                  backgroundColor: '#EEF2FF',
                  borderRadius: 3,
                  mt: 1,
                  zIndex: 2,
                  overflow: 'hidden'
                }}>
                  <Box sx={{
                    width: `${Math.min(card.additionalInfo.utilization, 100)}%`,
                    height: '100%',
                    backgroundColor: card.color,
                    borderRadius: 3,
                    transition: 'width 0.5s ease',
                  }} />
                </Box>
              )}

              {/* Background decoration */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                right: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                backgroundColor: `${card.color}0D`,
                zIndex: 1
              }} />
            </AnimatedCard>
          ))}
        </Box>

        {/* Enhanced Metrics Section */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(auto-fit, minmax(340px, 1fr))' },
          gap: 3,
          mb: 4
        }}>
          {/* Payment Methods Pie Chart Widget - Hidden for students */}
          {!isStudent && paymentMethodsData.length > 0 && (
            <AnimatedCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 2, sm: 3 } }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: 2.5, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: '#2E7D321A', color: '#2E7D32',
                }}>
                  <IconCreditCard size={26} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  طرق الدفع
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: { xs: 2, sm: 3 },
                flex: 1
              }}>
                <Box sx={{
                  width: { xs: 150, sm: 180 },
                  height: { xs: 150, sm: 180 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <PieChart
                    series={[{
                      data: paymentMethodsData.map((entry, index) => ({
                        id: index,
                        value: entry.value,
                        label: entry.name,
                        color: entry.color
                      })),
                      innerRadius: isMobile ? 42 : 50,
                      outerRadius: isMobile ? 68 : 80,
                      paddingAngle: 4,
                      valueFormatter: (item) => `₪${item.value.toLocaleString('en-US')}`
                    }]}
                    width={isMobile ? 150 : 180}
                    height={isMobile ? 150 : 180}
                    hideLegend
                  />
                </Box>

                <Box sx={{ flex: 1, width: '100%' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}>
                    {paymentMethodsData.map((method, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: { xs: 1.25, sm: 2 }, bgcolor: '#f8f9fa', borderRadius: 2, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: method.color, borderRadius: 1, flexShrink: 0 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            {method.name === 'نقداً' && <IconCoins size={16} color={method.color} />}
                            {method.name === 'فيزا' && <IconCreditCard size={16} color={method.color} />}
                            {method.name === 'شيك' && <IconClipboardList size={16} color={method.color} />}
                            {method.name === 'تحويل بنكي' && <IconTransfer size={16} color={method.color} />}
                            <Typography variant="body2" fontWeight="500" noWrap>{method.name}:</Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" fontWeight="bold" color={method.color} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                          {method.count} (₪{method.value.toLocaleString('en-US')})
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </AnimatedCard>
          )}

          {/* Inventory Widget - Hidden for students */}
          {!isStudent && dashboardData.inventory?.available && (
            <AnimatedCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95 }}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                minHeight: 300,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
                },  
                transition: 'all 0.3s ease'
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight="bold" color="#F57C00" sx={{ mb: 3 }}>
                عناصر المخزون المقتربة على الانتهاء
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dashboardData.inventory.low_quantity_items.slice(0, 4).map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#fef9e7', borderRadius: 2, border: '1px solid #F57C00' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconPackage size={16} color="#F57C00" />
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        {item.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold" color="#F57C00">
                      {item.count.toLocaleString('en-US')}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: '1px solid #eee' }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">إجمالي المخزون:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#F57C00">
                    {dashboardData.inventory.totalStock.toLocaleString('en-US')}
                  </Typography>
                </Box>
              </Box>
            </AnimatedCard>
          )}

          {/* Bus Metrics Widget - Hidden for students */}
          {!isStudent && dashboardData.buses && (
            <AnimatedCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                minHeight: 300,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight="bold" color="#1976D2" sx={{ mb: 3 }}>
                إحصائيات الحافلات
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconBus size={16} color="#1976D2" />
                    <Typography variant="body2" fontWeight="500">داخلية:</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="#1976D2">{dashboardData.buses.internal}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconTruck size={16} color="#7B1FA2" />
                    <Typography variant="body2" fontWeight="500">خارجية:</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color="#7B1FA2">{dashboardData.buses.external}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight="500">السعة الكلية:</Typography>
                  <Typography variant="body2" fontWeight="bold">{dashboardData.buses.totalCapacity}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#e8f5e8', borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight="500">المقاعد المتاحة:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#2E7D32">{dashboardData.buses.availableCapacity}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #2196F3' }}>
                  <Typography variant="body2" fontWeight="bold" color="#1976D2">نسبة الاستخدام:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#1976D2">
                    {dashboardData.buses.utilization?.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </AnimatedCard>
          )}
        </Box>
      </motion.div>

        {/* Finance Staff Section - date-range filtered finance cards, only for accounts with financial access */}
        {canViewFinanceRange && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: 2,
              mb: 2.5,
              p: 2.5,
              borderRadius: 3,
              backgroundColor: '#fff',
              border: '1px solid #E5E7EB',
            }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif', flexBasis: '100%' }}>
                قسم المالية حسب التاريخ
              </Typography>
              <TextField
                label="من تاريخ"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={financeStartDate}
                onChange={(e) => setFinanceStartDate(e.target.value)}
                size="small"
              />
              <TextField
                label="إلى تاريخ"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={financeEndDate}
                onChange={(e) => setFinanceEndDate(e.target.value)}
                size="small"
              />
              <Button
                variant="contained"
                onClick={applyFinanceRangeFilter}
                disabled={financeRangeLoading || !financeStartDate || !financeEndDate}
              >
                تطبيق
              </Button>
              <Button variant="text" onClick={() => { setCardPickerCategory('finance_range'); setCardPickerOpen(true); }}>
                إضافة بطاقات
              </Button>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3,
              mb: 3
            }}>
              {/* Students Financial Widget - installments collected vs uncollected in range */}
              <AnimatedCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  minHeight: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 2, sm: 3 } }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: 2.5, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: '#2E7D321A', color: '#2E7D32',
                  }}>
                    <IconCash size={26} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    مجمل أقساط الطلاب
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 2, sm: 3 },
                  flex: 1
                }}>
                  <Box sx={{
                    width: { xs: 150, sm: 180 },
                    height: { xs: 150, sm: 180 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <PieChart
                      series={(() => {
                        const collected = financeRangeStats?.receipts_collected?.total_amount || 0;
                        const uncollected = financeRangeStats?.uncollected_receipts?.total_amount || 0;
                        const hasData = collected > 0 || uncollected > 0;
                        return [{
                          data: hasData
                            ? [
                                { id: 0, value: collected, label: 'المحصل', color: '#2E7D32' },
                                { id: 1, value: uncollected, label: 'غير المحصل', color: '#D32F2F' }
                              ]
                            : [{ id: 0, value: 1, label: 'لا توجد بيانات', color: '#E5E7EB' }],
                          innerRadius: isMobile ? 42 : 50,
                          outerRadius: isMobile ? 68 : 80,
                          paddingAngle: 4,
                          valueFormatter: (item) => hasData ? `₪${item.value.toLocaleString('en-US')}` : 'لا توجد بيانات'
                        }];
                      })()}
                      width={isMobile ? 150 : 180}
                      height={isMobile ? 150 : 180}
                      hideLegend
                    />
                  </Box>

                  <Box sx={{ flex: 1, width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: { xs: 1.25, sm: 2 }, bgcolor: '#f8f9fa', borderRadius: 2, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#2E7D32', borderRadius: 1, flexShrink: 0 }} />
                          <Typography variant="body2" fontWeight="500" noWrap>المحصل:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold" color="#2E7D32" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                          ₪{(financeRangeStats?.receipts_collected?.total_amount || 0).toLocaleString('en-US')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: { xs: 1.25, sm: 2 }, bgcolor: '#f8f9fa', borderRadius: 2, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#D32F2F', borderRadius: 1, flexShrink: 0 }} />
                          <Typography variant="body2" fontWeight="500" noWrap>غير المحصل:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold" color="#D32F2F" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                          ₪{(financeRangeStats?.uncollected_receipts?.total_amount || 0).toLocaleString('en-US')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: { xs: 1.25, sm: 2 }, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #2196F3', minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="bold" color="#1976D2" noWrap>الإجمالي:</Typography>
                        <Typography variant="body2" fontWeight="bold" color="#1976D2" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                          ₪{(financeRangeStats?.installments?.total_amount || 0).toLocaleString('en-US')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </AnimatedCard>

              {/* Employee Salaries Widget - salaries paid vs outstanding for months within range */}
              <AnimatedCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  minHeight: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 2, sm: 3 } }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: 2.5, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: '#1976D21A', color: '#1976D2',
                  }}>
                    <IconUsers size={26} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    رواتب الموظفين
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 2, sm: 3 },
                  flex: 1
                }}>
                  <Box sx={{
                    width: { xs: 150, sm: 180 },
                    height: { xs: 150, sm: 180 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <PieChart
                      series={(() => {
                        const paid = financeRangeStats?.salaries?.total_paid || 0;
                        const outstanding = financeRangeStats?.salaries?.outstanding || 0;
                        const hasData = paid > 0 || outstanding > 0;
                        return [{
                          data: hasData
                            ? [
                                { id: 0, value: paid, label: 'المدفوع', color: '#2196F3' },
                                { id: 1, value: outstanding, label: 'المتبقي', color: '#FF9800' }
                              ]
                            : [{ id: 0, value: 1, label: 'لا توجد بيانات', color: '#E5E7EB' }],
                          innerRadius: isMobile ? 42 : 50,
                          outerRadius: isMobile ? 68 : 80,
                          paddingAngle: 4,
                          valueFormatter: (item) => hasData ? `₪${item.value.toLocaleString('en-US')}` : 'لا توجد بيانات'
                        }];
                      })()}
                      width={isMobile ? 150 : 180}
                      height={isMobile ? 150 : 180}
                      hideLegend
                    />
                  </Box>

                  <Box sx={{ flex: 1, width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: { xs: 1.25, sm: 2 }, bgcolor: '#f8f9fa', borderRadius: 2, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#1976D2', borderRadius: 1, flexShrink: 0 }} />
                          <Typography variant="body2" fontWeight="500" noWrap>المدفوع:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold" color="#1976D2" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                          ₪{(financeRangeStats?.salaries?.total_paid || 0).toLocaleString('en-US')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: { xs: 1.25, sm: 2 }, bgcolor: '#f8f9fa', borderRadius: 2, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#F57C00', borderRadius: 1, flexShrink: 0 }} />
                          <Typography variant="body2" fontWeight="500" noWrap>المتبقي:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold" color="#F57C00" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                          ₪{(financeRangeStats?.salaries?.outstanding || 0).toLocaleString('en-US')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: { xs: 1.25, sm: 2 }, bgcolor: '#e8f5e8', borderRadius: 2, border: '1px solid #2E7D32', minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="bold" color="#2E7D32" noWrap>الإجمالي:</Typography>
                        <Typography variant="body2" fontWeight="bold" color="#2E7D32" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                          ₪{(financeRangeStats?.salaries?.total || 0).toLocaleString('en-US')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </AnimatedCard>

              {/* Unpaid Transactions Widget - uncollected receipt vouchers in range */}
              <AnimatedCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  minHeight: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="#D32F2F" sx={{ mb: 2 }}>
                    سندات قبض غير محصلة
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <IconAlertTriangle size={20} color="#EF5350" />
                    <Typography variant="body2" color="text.secondary">
                      معاملات لم يتم استلامها بعد خلال الفترة المحددة
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ textAlign: 'center', py: 2, bgcolor: '#fef2f2', borderRadius: 2 }}>
                    <Typography variant="h3" fontWeight="bold" color="#D32F2F">
                      ₪{(financeRangeStats?.uncollected_receipts?.total_amount || 0).toLocaleString('en-US')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      المبلغ الإجمالي غير المستلم
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight="500">عدد المعاملات:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#D32F2F">
                        {financeRangeStats?.uncollected_receipts?.count || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight="500">النسبة المئوية:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#D32F2F">
                        {financeRangeStats?.installments?.count
                          ? ((financeRangeStats.uncollected_receipts.count / financeRangeStats.installments.count) * 100).toFixed(1)
                          : 0}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </AnimatedCard>
            </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 3,
              }}>
                {financeRangeCards.map((card, index) => (
                  <AnimatedCard
                    key={index}
                    elevation={0}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: index * 0.06, ease: 'easeOut' }}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: '#fff',
                      color: '#1E1B4B',
                      boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                      border: '1px solid #E5E7EB',
                      minHeight: 160,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <Box sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${card.color}1A`,
                        color: card.color,
                        flexShrink: 0,
                      }}>
                        {card.icon}
                      </Box>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        {card.title}
                      </Typography>
                    </Box>
                    <Typography fontWeight={800} sx={{ fontSize: '1.6rem', color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      {card.value}
                    </Typography>
                    {card.subtitle && (
                      <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>
                        {card.subtitle}
                      </Typography>
                    )}
                  </AnimatedCard>
                ))}
              </Box>
          </Box>
        )}

        {/* Monthly Chart - Hidden for students */}
        {!isStudent && (
        <MotionPaper
  elevation={0}
  initial={{ opacity: 0, y: 40 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7, delay: 0.35, ease: 'easeOut' }}
  sx={{
    borderRadius: 3,
    background: '#fff',
    border: '1px solid #E5E7EB',
    p: { xs: 2, md: 4 },
    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
    mb: 4,
    overflow: 'hidden',
    position: 'relative',
    minHeight: { xs: 420, md: 520 },
    '&:hover': {
      boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
    },
    transition: 'all 0.3s ease'
  }}
>
  {/* العنوان + القيم */}
  <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={2} mb={3}>
    <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
      <IconChartLine size={isMobile ? 24 : 28} color="#1976D2" />
      <Typography 
        variant={isMobile ? 'h6' : 'h5'} 
        fontWeight="bold" 
        color="#1976D2"
      >
        الإيرادات والمصروفات الشهرية
      </Typography>
    </Stack>
    <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }}>
      <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#2E7D32' }} />
        <Typography variant="subtitle2" color="#2E7D32" fontWeight="500">
          إجمالي الإيرادات:
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#2E7D32">
          ₪{dashboardData.monthlyData?.reduce((acc, cur) => acc + (cur.income || 0), 0).toLocaleString('en-US') || 0}
        </Typography>
      </Stack>
      <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#D32F2F' }} />
        <Typography variant="subtitle2" color="#D32F2F" fontWeight="500">
          إجمالي المصروفات:
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#D32F2F">
          ₪{dashboardData.monthlyData?.reduce((acc, cur) => acc + (cur.expenses || 0), 0).toLocaleString('en-US') || 0}
        </Typography>
      </Stack>
    </Stack>
  </Stack>

  {/* المنحنى */}
  <ResponsiveContainer width="100%" height={isMobile ? 280 : 360}>
    <LineChart data={dashboardData.monthlyData || []}
      margin={{ top: 10, right: 10, left: 10, bottom: isMobile ? 40 : 20 }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
      <XAxis 
        dataKey="month" 
        tick={{ fill: '#666', fontSize: isMobile ? 10 : 14, fontWeight: 600 }}
        angle={isMobile ? -45 : 0}
        textAnchor={isMobile ? 'end' : 'middle'}
        height={isMobile ? 60 : 40}
        axisLine={false}
        tickLine={false}
      />
      <YAxis 
        tickFormatter={(value) => `₪${value.toLocaleString('en-US')}`}
        tick={{ fill: '#666', fontSize: isMobile ? 10 : 14, fontWeight: 600 }}
        axisLine={false}
        tickLine={false}
      />
      <Tooltip 
        contentStyle={{
          borderRadius: '12px',
          border: 'none',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          background: '#fff',
          padding: '12px'
        }}
        formatter={(value) => `₪${value.toLocaleString('en-US')}`}
        labelStyle={{ fontWeight: 'bold', color: '#1976D2' }}
      />
      <Line 
        type="monotone" 
        dataKey="income" 
        name="الإيرادات" 
        stroke="#2E7D32" 
        strokeWidth={3} 
        dot={{ r: 4 }} 
        activeDot={{ r: 6 }} 
      />
      <Line 
        type="monotone" 
        dataKey="expenses" 
        name="المصروفات" 
        stroke="#D32F2F" 
        strokeWidth={3} 
        dot={{ r: 4 }} 
        activeDot={{ r: 6 }} 
      />
    </LineChart>
  </ResponsiveContainer>
</MotionPaper>
        )}


        {/* Add Destination Modal */}
        <Dialog 
          open={openDestinationModal} 
          onClose={() => setOpenDestinationModal(false)} 
          maxWidth="xs" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 800,
            color: '#1E1B4B',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            borderBottom: '1px solid #E5E7EB',
            pb: 2
          }}>
            إضافة جهة الصرف المعتمدة
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }} mt={2}>
            <Stack spacing={3}>
              <TextField
                label="الاسم"
                fullWidth
                value={newDestination.name}
                onChange={(e) => setNewDestination({ ...newDestination, name: e.target.value })}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
  <Select
    value={newDestination.phoneCode}
    onChange={(e) => setNewDestination({ ...newDestination, phoneCode: e.target.value })}
    sx={{ 
      minWidth: 120,
      '& .MuiOutlinedInput-root': { borderRadius: 2 }
    }}
  >
    {PHONE_CODE_OPTIONS.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.flag} {option.label}
      </MenuItem>
    ))}
  </Select>
  <TextField
    label="رقم الهاتف"
    fullWidth
    value={newDestination.phone_number}
    onChange={(e) => setNewDestination({ ...newDestination, phone_number: e.target.value })}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: 2,
      }
    }}
  />
</Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => setOpenDestinationModal(false)}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={async () => {
                await handleAddDestination();
                setOpenDestinationModal(false);
              }}
              variant="contained"
              sx={{
                borderRadius: 2.5,
                px: 3,
                py: 1,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                backgroundColor: '#4338CA',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#3730A3',
                  boxShadow: 'none',
                }
              }}
            >
              إضافة
            </Button>
          </DialogActions>
        </Dialog>
     </Box>
    </Box>
  );
};

export default DashboardPage;